import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { LinkedInPost, LinkedInPostFilter, LinkedInPostFormData, LinkedInMedia, LinkedInPostType, LinkedInPostStatus } from "@/types/linkedin";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Json } from "@/integrations/supabase/types";

export const useLinkedInPosts = () => {
  const [posts, setPosts] = useState<LinkedInPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<LinkedInPostFilter>({});
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchPosts(filter);
  }, [filter]);

  const fetchPosts = async (filter: LinkedInPostFilter) => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase.from("linkedin_posts").select("*");

      // Apply filters
      if (filter.status) {
        query = query.eq("status", filter.status);
      }
      
      if (filter.user_id) {
        query = query.eq("user_id", filter.user_id);
      }
      
      if (filter.from_date) {
        query = query.gte("created_at", filter.from_date);
      }
      
      if (filter.to_date) {
        query = query.lte("created_at", filter.to_date);
      }
      
      if (filter.search_term) {
        query = query.or(`content_text.ilike.%${filter.search_term}%,review_notes.ilike.%${filter.search_term}%`);
      }

      // Execute query
      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;

      // Transform data to match our interface
      const transformedData: LinkedInPost[] = data.map(post => {
        let mediaUrls: LinkedInMedia[] = [];
        
        // Handle media_url from database which could be JSON string or already parsed object
        if (post.media_url) {
          try {
            // Parse the media_url data appropriately
            if (Array.isArray(post.media_url)) {
              // If it's already an array, we need to ensure each item has the correct shape
              mediaUrls = (post.media_url as Json[]).map(item => {
                // Validate and convert each item to LinkedInMedia
                const media: LinkedInMedia = {
                  url: typeof item === 'object' && item !== null ? (item as any).url || '' : '',
                  type: typeof item === 'object' && item !== null ? (item as any).type || 'image' : 'image',
                  id: typeof item === 'object' && item !== null ? (item as any).id : undefined
                };
                return media;
              });
            } else if (typeof post.media_url === 'string' && post.media_url.trim() !== '') {
              // Parse JSON string
              const parsedMedia = JSON.parse(post.media_url) as any[];
              if (Array.isArray(parsedMedia)) {
                mediaUrls = parsedMedia.map(item => ({
                  url: item.url || '',
                  type: item.type || 'image',
                  id: item.id
                }));
              }
            }
          } catch (e) {
            console.error("Error parsing media_url:", e);
            mediaUrls = [];
          }
        }
        
        return {
          ...post,
          media_url: mediaUrls,
          // Ensure post_type is cast to the correct type
          post_type: post.post_type as LinkedInPostType,
          // Ensure status is cast to the correct type
          status: post.status as LinkedInPostStatus
        } as LinkedInPost;
      });

      setPosts(transformedData);
    } catch (err: any) {
      console.error("Error fetching LinkedIn posts:", err);
      setError(err.message || "Failed to load LinkedIn posts");
      toast({
        title: "Error",
        description: "Failed to load LinkedIn posts. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createPost = async (formData: LinkedInPostFormData): Promise<LinkedInPost | null> => {
    try {
      if (!user?.id) throw new Error("User not authenticated");

      // Convert media_url to a JSON format that Supabase can store
      const mediaUrl = formData.media_url || [];

      // Prepare data for insert
      const newPostData = {
        content_text: formData.content_text,
        post_type: formData.post_type,
        status: formData.status || 'draft',
        user_id: user.id,
        // Convert Date to ISO string for storage
        scheduled_at: formData.scheduled_at ? formData.scheduled_at.toISOString() : null,
        // Store media_url as plain JSON - we need to stringify the array to store it properly
        media_url: mediaUrl.length > 0 ? JSON.stringify(mediaUrl) : null
      };

      const { data, error } = await supabase
        .from("linkedin_posts")
        .insert([newPostData])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "LinkedIn post created successfully",
      });

      // Refresh posts
      fetchPosts(filter);
      
      // Transform returned post data to match our interface
      const transformedPost: LinkedInPost = {
        ...data,
        // Process the media_url to ensure it's in the right format
        media_url: data.media_url ? 
          (typeof data.media_url === 'string' ? 
            JSON.parse(data.media_url) : 
            Array.isArray(data.media_url) ? 
              data.media_url.map((item: any) => ({
                url: item.url || '',
                type: item.type || 'image',
                id: item.id
              })) 
            : []
          ) : [],
        post_type: data.post_type as LinkedInPostType,
        status: data.status as LinkedInPostStatus
      };
      
      return transformedPost;
    } catch (err: any) {
      console.error("Error creating LinkedIn post:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to create LinkedIn post",
        variant: "destructive",
      });
      return null;
    }
  };

  const updatePost = async (id: string, formData: Partial<LinkedInPostFormData>): Promise<boolean> => {
    try {
      // Prepare the data for update
      const updateData: Record<string, any> = {};
      
      if (formData.content_text !== undefined) {
        updateData.content_text = formData.content_text;
      }
      
      if (formData.post_type !== undefined) {
        updateData.post_type = formData.post_type;
      }
      
      if (formData.status !== undefined) {
        updateData.status = formData.status;
      }
      
      // Handle scheduled_at date conversion
      if (formData.scheduled_at !== undefined) {
        updateData.scheduled_at = formData.scheduled_at ? formData.scheduled_at.toISOString() : null;
      }
      
      // Handle media_url - convert to JSON string for storage
      if (formData.media_url !== undefined) {
        updateData.media_url = formData.media_url && formData.media_url.length > 0 
          ? JSON.stringify(formData.media_url) 
          : null;
      }

      const { error } = await supabase
        .from("linkedin_posts")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "LinkedIn post updated successfully",
      });

      // Refresh posts
      fetchPosts(filter);
      
      return true;
    } catch (err: any) {
      console.error("Error updating LinkedIn post:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to update LinkedIn post",
        variant: "destructive",
      });
      return false;
    }
  };

  const deletePost = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("linkedin_posts")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "LinkedIn post deleted successfully",
      });

      // Refresh posts
      fetchPosts(filter);
      
      return true;
    } catch (err: any) {
      console.error("Error deleting LinkedIn post:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to delete LinkedIn post",
        variant: "destructive",
      });
      return false;
    }
  };

  const schedulePost = async (id: string, scheduledAt: Date): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("linkedin_posts")
        .update({
          status: 'scheduled',
          scheduled_at: scheduledAt.toISOString()
        })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "LinkedIn post scheduled successfully",
      });

      // Refresh posts
      fetchPosts(filter);
      
      return true;
    } catch (err: any) {
      console.error("Error scheduling LinkedIn post:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to schedule LinkedIn post",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    posts,
    loading,
    error,
    filter,
    setFilter,
    fetchPosts,
    createPost,
    updatePost,
    deletePost,
    schedulePost
  };
};
